import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, take, map } from 'rxjs';

export const authGuard: CanActivateFn = () => {
  const msalService = inject(MsalService);
  const msalBroadcastService = inject(MsalBroadcastService);

  return msalBroadcastService.inProgress$.pipe(
    filter((status: InteractionStatus) => status === InteractionStatus.None),
    take(1),
    map(() => {
      const accounts = msalService.instance.getAllAccounts();

      if (accounts.length > 0) {
        // User is authenticated
        return true;
      }

      // User is not authenticated, redirect to MSAL login
      msalService.loginRedirect();
      return false;
    })
  );
};

// Guard that checks for specific roles/groups
export const roleGuard = (allowedGroups: string[]): CanActivateFn => {
  return () => {
    const msalService = inject(MsalService);
    const msalBroadcastService = inject(MsalBroadcastService);
    const router = inject(Router);

    return msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      map(() => {
        const activeAccount = msalService.instance.getActiveAccount();

        if (!activeAccount) {
          msalService.loginRedirect();
          return false;
        }

        const claims = activeAccount.idTokenClaims as Record<string, unknown> | undefined;
        const userGroups = (claims?.['groups'] as string[]) ?? [];

        const hasRequiredRole = allowedGroups.some(group => userGroups.includes(group));

        if (!hasRequiredRole) {
          // User doesn't have required role, redirect to unauthorized page
          router.navigate(['/unauthorized']);
          return false;
        }

        return true;
      })
    );
  };
};
